import React, { useState, useEffect } from 'react';
import {
  shouldShowConsentBanner,
  acceptAllConsent,
  getConsent,
} from '../utils/cookieConsent';

/**
 * Cookie Consent Banner Component
 * Displays on first visit and provides options to accept all or manage preferences
 * Accessible, mobile-responsive, and styled to match Book A Therapy brand
 */
const CookieConsentBanner = ({ onOpenPreferences }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if banner should be shown
    if (shouldShowConsentBanner()) {
      // Small delay for smooth entrance animation
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 500);
    }

    // Listen for consent changes from other tabs/windows
    const handleStorageChange = () => {
      if (!shouldShowConsentBanner()) {
        handleClose();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('consentUpdated', handleClose);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('consentUpdated', handleClose);
    };
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleAcceptAll = () => {
    acceptAllConsent();
    handleClose();
  };

  const handleManagePreferences = () => {
    if (onOpenPreferences) {
      onOpenPreferences();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop overlay for mobile */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998] transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />

      {/* Cookie consent card */}
      <div
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        aria-modal="false"
        className={`fixed bottom-6 right-6 z-[9999] max-w-md transition-all duration-500 ease-out ${
          isAnimating ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
        }`}
      >
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200">
          <div className="p-6">
            {/* Header with icon */}
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2
                  id="cookie-banner-title"
                  className="text-lg font-bold text-slate-900 mb-1"
                >
                  We Value Your Privacy
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                  🍪 Cookie Notice
                </span>
              </div>
            </div>

            {/* Description */}
            <p
              id="cookie-banner-description"
              className="text-sm text-slate-600 leading-relaxed mb-5"
            >
              We use cookies to enhance your experience and analyze our traffic.
              You can customize your preferences or accept all.
            </p>

            {/* Action buttons */}
            <div className="flex flex-col gap-2.5">
              <button
                onClick={handleAcceptAll}
                className="w-full px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label="Accept all cookies"
              >
                Accept All Cookies
              </button>

              <button
                onClick={handleManagePreferences}
                className="w-full px-4 py-2.5 text-sm font-semibold text-indigo-600 bg-white border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label="Manage cookie preferences"
              >
                Manage Preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;
